import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import {
  findTrunkImpairmentScaleLockedItems,
  TRUNK_IMPAIRMENT_SCALE_FIRST_ITEM,
} from 'src/app/utils/questionnaire-locked-items';

interface TrunkImpairmentScaleScoreField {
  controlName: string;
  maxScore: number;
  isTotal: boolean;
  // The items it adds up, or for the total the score fields it adds up.
  parts: string[];
}

// The points are the number in "(n)" at the start of the answer, 0 to 3.
// The 0-2 regex of Mini-BESTest and Fugl-Meyer would lose item 809's (3).
const TRUNK_IMPAIRMENT_SCALE_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const TRUNK_IMPAIRMENT_SCALE_MAX_POINTS = 3;

// Titles separate the instruction with "||" or "‖".
export const TRUNK_IMPAIRMENT_SCALE_TITLE_SEPARATOR = /\|\||‖/;

// In calculation order: the total comes after the subscales it adds up.
const TRUNK_IMPAIRMENT_SCALE_SCORE_FIELDS: TrunkImpairmentScaleScoreField[] = [
  // Static sitting balance
  {
    controlName: 'question810',
    maxScore: 7,
    isTotal: false,
    parts: ['question807', 'question808', 'question809'],
  },
  // Dynamic sitting balance
  {
    controlName: 'question821',
    maxScore: 10,
    isTotal: false,
    parts: [
      'question811',
      'question812',
      'question813',
      'question814',
      'question815',
      'question816',
      'question817',
      'question818',
      'question819',
      'question820',
    ],
  },
  // Co-ordination
  {
    controlName: 'question826',
    maxScore: 6,
    isTotal: false,
    parts: ['question822', 'question823', 'question824', 'question825'],
  },
  // Total TIS
  {
    controlName: 'question827',
    maxScore: 23,
    isTotal: true,
    parts: ['question810', 'question821', 'question826'],
  },
];

// Every item, in form order.
const TRUNK_IMPAIRMENT_SCALE_ITEMS = TRUNK_IMPAIRMENT_SCALE_SCORE_FIELDS.filter(
  (field) => !field.isTotal,
).flatMap((field) => field.parts);

@Component({
  selector: 'app-trunk-impairment-scale-questions',
  templateUrl: './trunk-impairment-scale-questions.component.html',
  styleUrl: './trunk-impairment-scale-questions.component.css',
  standalone: false,
})
export class TrunkImpairmentScaleQuestionsComponent
  implements OnInit, OnDestroy
{
  // The current page, which is the one shown.
  @Input() elements: IQuestionnaireQuestion[] = [];
  // Every page: the total adds up answers from other pages.
  @Input() allElements: IQuestionnaireQuestion[] = [];
  // The current page's title.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private lockedItems = new Set<string>();
  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return (
      title?.split(TRUNK_IMPAIRMENT_SCALE_TITLE_SEPARATOR)[0]?.trim() ?? ''
    );
  }

  getQuestionInstruction(title?: string): string {
    return (
      title?.split(TRUNK_IMPAIRMENT_SCALE_TITLE_SEPARATOR)[1]?.trim() ?? ''
    );
  }

  // An item zeroed by an earlier answer is shown dimmed.
  isLocked(element: IQuestionnaireQuestion): boolean {
    return this.lockedItems.has(element.name);
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && !!this.getScoreField(element.name);
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return this.getScoreField(element.name)?.isTotal ?? false;
  }

  // The template has no maximums, so the UI supplies them.
  getMaxScore(controlName: string): number | null {
    return this.getScoreField(controlName)?.maxScore ?? null;
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private getScoreField(
    controlName: string,
  ): TrunkImpairmentScaleScoreField | undefined {
    return TRUNK_IMPAIRMENT_SCALE_SCORE_FIELDS.find(
      (field) => field.controlName === controlName,
    );
  }

  private recalculateScores(): void {
    // The backend's rules: a 0 zeroes the items that build on it, and a 0 on
    // item 1 zeroes every other item.
    this.lockedItems = findTrunkImpairmentScaleLockedItems(
      this.allElements,
      (item) => this.formGroup.get(item)?.value,
    );
    this.applyLocks();

    const scores = new Map<string, number | null>();
    for (const field of TRUNK_IMPAIRMENT_SCALE_SCORE_FIELDS) {
      const score = this.sumParts(field.parts, scores);
      scores.set(field.controlName, score);
      this.setLockedScore(field.controlName, score);
    }

    this.cdr.detectChanges();
  }

  // Like Fugl-Meyer's part V: a locked item keeps an earlier answer in the
  // form, but it does not count and is not saved, and the item is unlocked
  // only when the form itself can be edited. Item 1 is never locked.
  private applyLocks(): void {
    const editable = this.formGroup.get(
      TRUNK_IMPAIRMENT_SCALE_FIRST_ITEM,
    )?.enabled;

    for (const item of TRUNK_IMPAIRMENT_SCALE_ITEMS) {
      const control = this.formGroup.get(item);
      if (this.lockedItems.has(item)) {
        control?.disable({ emitEvent: false });
      } else if (editable && control?.disabled) {
        control.enable({ emitEvent: false });
      }
    }
  }

  // Live sum of the parts answered so far, or null while none is, so an
  // untouched subscale stays empty and is not saved. A locked item counts 0.
  private sumParts(
    parts: string[],
    scores: Map<string, number | null>,
  ): number | null {
    const points = parts
      .map((part) => {
        if (scores.has(part)) return scores.get(part) ?? null;
        if (this.lockedItems.has(part)) return 0;
        return this.getSelectedPoints(part);
      })
      .filter((point): point is number => point !== null);

    return points.length ? points.reduce((sum, point) => sum + point, 0) : null;
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.allElements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(
      TRUNK_IMPAIRMENT_SCALE_POINTS_REGEX,
    );
    const points = match ? Number(match[1]) : null;

    return points !== null && points <= TRUNK_IMPAIRMENT_SCALE_MAX_POINTS
      ? points
      : null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(score === null ? '' : String(score), {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
